import React, { useEffect, useState } from 'react';
import OnboardingHeader from '@/shared/components/navigation/OnboardingHeader';
import Button from '@/shared/components/Button';
import { useAuth } from '@/services/auth/AuthProvider';
import { ProfileFormData, StylingPreference } from '@/shared/types/stylist';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useMutation, ApolloError } from '@apollo/client';
import {
  CREATE_CLIENT_STRIPE_CUSTOMER,
  CREATE_USER,
  UPDATE_CLIENT,
} from '@/shared/mutations';
import {
  MutationCreateUserArgs,
  MutationResult,
  MutationUpdateClientArgs,
  UserType,
} from '@/shared/types/generated';
import CloudinaryService from '@/services/cloudinary/Cloudinary.service';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import useDimensions from '@/shared/hooks/useDimensions';
import { ButtonSelectItem } from '../onboarding/components/SelectButtons';
import { useNavigation } from 'expo-router';
import { GET_USER } from '@/shared/queries';
import { NAME_MAX_LENGTH } from '@/shared/constants/utility';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import {
  getCurrentTimeZone,
  resetNavigationToRoute,
  shouldSurfaceGraphQLError,
} from '@/shared/utils';
import CreateProfile from '../onboarding/screens/CreateProfile';
import Progress from '../onboarding/components/Progress';
import SelectPreferences from '../onboarding/screens/SelectPreferences';
import AddPayment from './components/AddPayment';
import AboutYou from './components/AboutYou';
import {
  OnboardingBottomContainer,
  OnboardingButtonContainer,
  OnboardingContentContainer,
  OnboardingFormArea,
  OnboardingContainerStyles,
} from '../../components/onboarding/styledComponents';
import { useApollo } from '@/services/apollo/ApolloProvider';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import ScrollableView from '@/shared/components/ScrollableView';
import {
  ADD_PAYMENT_STEP,
  ABOUT_YOU_STEP,
  clientOnboardingDescriptionTextOptions,
  CREATE_ACCOUNT_STEP,
  FINISHED_STEP,
  SELECT_PREFERENCES_STEP,
} from '@/shared/constants/client-onboarding';
import { clientOnboardingTitleTextOptions } from '@/shared/constants/client-onboarding';
import { green, sand, white } from '@/shared/styles/colors';
import OnboardingCompleteButtons from './components/OnboardingCompleteButtons';
import { css } from '@emotion/native';
import { ROUTES } from '@/shared/constants/routes';
import { NavigationProp, ParamListBase } from '@react-navigation/native';
import useRouteThemeStore from '@/stores/useRouteThemeStore';

const Onboarding = () => {
  const { setSnackbarState } = useStore();
  const { auth0User, getAccessToken } = useAuth();
  const { logOut } = useApollo();
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const { width } = useDimensions();
  const isDesktop = useIsDesktop();
  const scrollToTop = useScrollToTop();
  const { setOverride, clearOverride } = useRouteThemeStore();

  const [phoneNumberError, setPhoneNumberError] = useState(false);

  const [createUser] = useMutation<
    MutationResult<'createUser'>,
    MutationCreateUserArgs
  >(CREATE_USER);

  const [createClientStripeCustomer] = useMutation<
    MutationResult<'createClientStripeCustomer'>
  >(CREATE_CLIENT_STRIPE_CUSTOMER, {
    refetchQueries: [{ query: GET_USER }],
  });

  const [updateClient] = useMutation<
    MutationResult<'updateClient'>,
    MutationUpdateClientArgs
  >(UPDATE_CLIENT, {
    refetchQueries: [{ query: GET_USER }],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [checkboxError, setCheckboxError] = useState(false);
  const isStep3 = progress === FINISHED_STEP;

  const [profileFormData, setProfileFormData] = useState<ProfileFormData>({
    profileImage: '',
    firstName: '',
    lastName: '',
    birthDate: undefined,
    phoneNumber: '',
    isCheckBoxChecked: false,
    shareAge: false,
  });
  const [selectedStylingPreference, setSelectedStylingPreference] = useState<
    ButtonSelectItem[]
  >([]);
  const [bio, setBio] = useState('');
  const [sizingDetails, setSizingDetails] = useState('');
  const [height, setHeight] = useState('');

  const nameDisableCondition =
    profileFormData.firstName.length > NAME_MAX_LENGTH ||
    profileFormData.lastName.length > NAME_MAX_LENGTH;

  // Add age validation helper
  const isUnderEighteen = (birthDate: Date | undefined): boolean => {
    if (!birthDate || isNaN(birthDate.getTime())) return false;

    const eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

    return birthDate > eighteenYearsAgo;
  };

  const ageError = isUnderEighteen(profileFormData.birthDate);

  const stepOneDisableCondition =
    !profileFormData.firstName ||
    !profileFormData.lastName ||
    !profileFormData.birthDate ||
    !profileFormData.profileImage ||
    nameDisableCondition ||
    !profileFormData.isCheckBoxChecked ||
    ageError;

  // Override the safe area theming for step 3
  useEffect(() => {
    if (isStep3) {
      setOverride({
        topSafeAreaBackground: green,
        bottomSafeAreaBackground: green,
        statusBarStyle: 'light-content',
      });
    } else {
      clearOverride();
    }
    // Omit the functions from dep array to avoid update depth exceeded warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStep3]);

  const getDisabledState = () => {
    switch (progress) {
      case CREATE_ACCOUNT_STEP: {
        return stepOneDisableCondition;
      }
      case ADD_PAYMENT_STEP: {
        return false;
      }
      case SELECT_PREFERENCES_STEP: {
        return false;
      }
      case ABOUT_YOU_STEP: {
        return false; // Optional fields, so never disabled
      }
      default: {
        return true;
      }
    }
  };

  const getButtonText = () => {
    if (isLoading) return '';

    switch (progress) {
      case ADD_PAYMENT_STEP:
        return 'Skip for now';
      case ABOUT_YOU_STEP:
        return 'Create Account';
      default:
        return 'Continue';
    }
  };

  const getProfilePhotoUrl = async () => {
    try {
      const token = await getAccessToken();
      const result = await CloudinaryService.uploadToCloudinary(
        token,
        profileFormData.profileImage,
      );

      return result.secure_url;
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Failed to upload profile photo. Please try again.',
        severity: 'error',
      });
      SentryService.captureError('Error uploading profile photo', error);
      throw error;
    }
  };

  const createNewUser = async () => {
    if (!auth0User?.sub || !auth0User?.email) {
      throw new Error('Missing Auth0 user data');
    }
    const profilePicUrl = await getProfilePhotoUrl();

    const phoneNumber = Boolean(profileFormData.phoneNumber)
      ? `+1${profileFormData.phoneNumber}`
      : null;

    await createUser({
      variables: {
        input: {
          auth0Id: auth0User.sub,
          email: auth0User.email,
          firstName: profileFormData.firstName,
          lastName: profileFormData.lastName,
          birthDate: profileFormData.birthDate,
          timeZone: getCurrentTimeZone(),
          profilePicUrl,
          phoneNumber,
          type: UserType.Client,
          shareAge: profileFormData.shareAge ?? false,
        },
      },
    });
  };

  const saveClient = async () => {
    const input: {
      aesthetics: string[];
      bio?: string;
      sizingDetails?: string;
      height?: string;
      isNewUser?: boolean;
    } = {
      aesthetics: selectedStylingPreference as string[],
      isNewUser: true, // This is always true during onboarding
    };

    if (bio) {
      input.bio = bio;
    }
    if (sizingDetails) {
      input.sizingDetails = sizingDetails;
    }
    if (height) {
      input.height = height;
    }

    await updateClient({
      variables: {
        input,
      },
    });
  };

  const onContinuePress = async () => {
    switch (progress) {
      case CREATE_ACCOUNT_STEP: {
        if (
          profileFormData.maskedPhoneNumber &&
          !isValidPhoneNumber(`+1${profileFormData.maskedPhoneNumber}`, 'US')
        ) {
          setPhoneNumberError(true);
          return;
        }

        if (profileFormData.isCheckBoxChecked === false) {
          setCheckboxError(true);
          return;
        }

        try {
          setIsLoading(true);
          await createNewUser();
          await createClientStripeCustomer();

          break;
        } catch (error) {
          if (error instanceof ApolloError) {
            setSnackbarState({
              open: true,
              message: shouldSurfaceGraphQLError(error)
                ? error.graphQLErrors?.[0]?.message
                : 'There was a problem creating your profile. Please try again.',
              severity: 'error',
            });
            SentryService.captureError('Error creating user', error);
          }
          return;
        } finally {
          setIsLoading(false);
        }
      }
      case SELECT_PREFERENCES_STEP: {
        // Just proceed to next step - aesthetics will be saved in final step
        break;
      }
      case ABOUT_YOU_STEP: {
        try {
          setIsLoading(true);
          await saveClient();
          setIsLoading(false);
          break;
        } catch (error) {
          setSnackbarState({
            open: true,
            message:
              'An error occurred while saving your profile. Please try again.',
            severity: 'error',
          });
          SentryService.captureError('Error saving profile details', error);
          setIsLoading(false);
          return;
        }
      }
      default: {
        break;
      }
    }
    setProgress(progress + 1);
    scrollToTop();
  };

  const renderStepContent = () => {
    switch (progress) {
      case CREATE_ACCOUNT_STEP: {
        return (
          <CreateProfile
            formData={profileFormData}
            setFormData={setProfileFormData}
            phoneNumberError={phoneNumberError}
            setPhoneNumberError={setPhoneNumberError}
            setCheckboxError={setCheckboxError}
            checkboxError={checkboxError}
            ageError={ageError}
          />
        );
      }
      case ADD_PAYMENT_STEP: {
        return <AddPayment setProgress={setProgress} />;
      }
      case SELECT_PREFERENCES_STEP: {
        return (
          <SelectPreferences
            selectedItems={selectedStylingPreference}
            setSelectedItems={setSelectedStylingPreference}
            enumArray={Object.values(StylingPreference)}
            showDescription
          />
        );
      }
      case ABOUT_YOU_STEP: {
        return (
          <AboutYou
            bio={bio}
            setBio={setBio}
            sizingDetails={sizingDetails}
            setSizingDetails={setSizingDetails}
            height={height}
            setHeight={setHeight}
          />
        );
      }
      case FINISHED_STEP: {
        return (
          <OnboardingCompleteButtons
            onFirstButtonClick={() => {
              resetNavigationToRoute(
                navigation,
                ROUTES.EXPLORE.path.toString(),
              );
            }}
            firstButtonText="Explore Stylists"
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <>
      <ScrollableView
        contentContainerStyle={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
        scrollEnabled
        style={[
          OnboardingContainerStyles,
          { backgroundColor: isStep3 ? green : sand },
        ]}
      >
        <OnboardingHeader
          progress={progress}
          showDarkColors={progress !== FINISHED_STEP}
          hideBackButton={isStep3}
          onPress={() => logOut()}
          authText="Log Out"
          setProgress={setProgress}
        />
        <OnboardingContentContainer isDesktop={isDesktop}>
          <Progress
            progress={progress}
            titleText={clientOnboardingTitleTextOptions}
            descriptionText={clientOnboardingDescriptionTextOptions}
            altStyle={isStep3}
            isRequired={progress === CREATE_ACCOUNT_STEP}
          />
          <OnboardingBottomContainer isDesktop={isDesktop}>
            <OnboardingFormArea isDesktop={isDesktop}>
              {renderStepContent()}
            </OnboardingFormArea>
          </OnboardingBottomContainer>
        </OnboardingContentContainer>
      </ScrollableView>
      {progress !== FINISHED_STEP && (
        <OnboardingButtonContainer isDesktop={isDesktop} width={width}>
          <Button
            text={getButtonText()}
            onPress={onContinuePress}
            disabled={getDisabledState() || isLoading}
            loading={isLoading}
            customStyle={
              progress === ADD_PAYMENT_STEP ? paymentDetailsButtonStyles : {}
            }
            customLabelStyle={
              progress === ADD_PAYMENT_STEP
                ? paymentDetailsButtonLabelStyles
                : {}
            }
          />
        </OnboardingButtonContainer>
      )}
    </>
  );
};

export default Onboarding;

const paymentDetailsButtonStyles = css({
  backgroundColor: white,
  borderColor: green,
});

const paymentDetailsButtonLabelStyles = css({
  color: green,
});
